import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { CheckCircle2, Inbox, Loader2, Mail, Send } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const timeFmt = (iso) => new Date(iso).toLocaleString();

export const Leads = ({ onUnreadChange }) => {
  const [leads, setLeads] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [sending, setSending] = useState(false);

  const sendReply = async (lead) => {
    setSending(true);
    try {
      const { data } = await api.post(`/admin/leads/${lead.id}/reply`, { message: replyText });
      setLeads((ls) => ls.map((l) => (l.id === lead.id ? { ...l, reply: data.reply } : l)));
      setReplyText("");
      toast.success(`Reply sent to ${lead.email}`);
    } catch {
      toast.error("Could not send the reply");
    } finally {
      setSending(false);
    }
  };

  useEffect(() => {
    api.get("/admin/leads").then((r) => {
      setLeads(r.data.leads);
      onUnreadChange?.(r.data.unread);
    }).catch(() => setLeads([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const open = async (lead) => {
    setOpenId((id) => (id === lead.id ? null : lead.id));
    setReplyText("");
    if (!lead.read) {
      setLeads((ls) => {
        const next = ls.map((l) => (l.id === lead.id ? { ...l, read: true } : l));
        onUnreadChange?.(next.filter((l) => !l.read).length);
        return next;
      });
      api.patch(`/admin/leads/${lead.id}`).catch(() => {});
    }
  };

  if (!leads)
    return (
      <div className="flex items-center justify-center py-32">
        <Loader2 className="w-6 h-6 text-cyan-neon animate-spin" />
      </div>
    );

  const unread = leads.filter((l) => !l.read).length;

  return (
    <div className="rounded-xl bg-[#0B101A] border border-white/8 p-6">
      <div className="flex items-center gap-2 mb-1">
        <Inbox className="w-4 h-4 text-cyan-neon" />
        <h3 className="font-display text-lg text-white">Lead inbox</h3>
        {unread > 0 && (
          <span data-testid="leads-unread-count" className="ml-1 px-2 py-0.5 rounded-full bg-cyan-neon/15 border border-cyan-neon/30 font-mono text-[10px] text-cyan-neon">
            {unread} new
          </span>
        )}
      </div>
      <p className="text-sm text-slate-400 mb-5">Every contact-form submission, newest first. Opening a lead marks it read.</p>

      {leads.length === 0 ? (
        <p data-testid="leads-empty" className="font-mono text-xs text-slate-500 py-6">No leads yet — they'll appear here the moment someone submits the contact form.</p>
      ) : (
        <div data-testid="leads-list" className="space-y-2">
          {leads.map((l, i) => (
            <motion.div
              key={l.id}
              data-testid={`lead-row-${l.id}`}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: Math.min(i * 0.03, 0.3) }}
              className={`rounded-lg border ${l.read ? "bg-[#040914] border-white/5" : "bg-cyan-neon/5 border-cyan-neon/20"}`}
            >
              <button
                data-testid={`lead-open-${l.id}`}
                onClick={() => open(l)}
                className="w-full flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-3 text-left"
              >
                {!l.read && <span data-testid={`lead-unread-dot-${l.id}`} className="w-2 h-2 rounded-full bg-cyan-neon animate-pulse-glow shrink-0" />}
                <span className={`text-sm ${l.read ? "text-slate-300" : "text-white font-semibold"}`}>{l.name}</span>
                <span className="font-mono text-xs text-slate-500">{l.email}</span>
                <span className="text-xs text-slate-500 truncate max-w-[24rem] hidden md:inline">{l.message}</span>
                <span className="ml-auto font-mono text-[10px] text-slate-600">{timeFmt(l.created_at)}</span>
              </button>
              {openId === l.id && (
                <div className="px-4 pb-4">
                  <p className="text-sm text-slate-300 leading-relaxed whitespace-pre-wrap rounded-lg bg-[#040914] border border-white/5 p-4">{l.message}</p>
                  {l.reply ? (
                    <div data-testid={`lead-replied-${l.id}`} className="mt-3 rounded-lg bg-[#39FF14]/5 border border-[#39FF14]/20 p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <CheckCircle2 className="w-3.5 h-3.5 text-[#39FF14]" />
                        <span className="font-mono text-[10px] tracking-wider uppercase text-[#39FF14]">
                          Replied {timeFmt(l.reply.sent_at)}
                        </span>
                      </div>
                      <p className="text-sm text-slate-300 leading-relaxed whitespace-pre-wrap">{l.reply.message}</p>
                    </div>
                  ) : (
                    <div className="mt-3">
                      <textarea
                        data-testid={`lead-reply-input-${l.id}`}
                        value={openId === l.id ? replyText : ""}
                        onChange={(e) => setReplyText(e.target.value)}
                        rows={3}
                        placeholder={`Write your reply to ${l.name.split(" ")[0]}…`}
                        className="w-full px-4 py-3 rounded-lg bg-[#040914] border border-white/10 text-white text-sm placeholder:text-slate-600 focus:border-cyan-neon/50 focus:outline-none transition-colors"
                      />
                      <div className="mt-2 flex items-center gap-3">
                        <button
                          data-testid={`lead-send-reply-${l.id}`}
                          onClick={() => sendReply(l)}
                          disabled={sending || replyText.trim().length < 2}
                          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-cyan-neon text-[#040914] text-sm font-semibold hover:brightness-110 transition-[filter] disabled:opacity-50"
                        >
                          {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                          Send reply
                        </button>
                        <a
                          data-testid={`lead-reply-${l.id}`}
                          href={`mailto:${l.email}?subject=Re: your message to ClickShield AI`}
                          className="inline-flex items-center gap-2 text-xs text-slate-500 hover:text-cyan-neon transition-colors"
                        >
                          <Mail className="w-3.5 h-3.5" /> or use your email client
                        </a>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};
